import type { FullSeafarerData } from '@/lib/types/seafarer';

/**
 * Build a compact text summary of the seafarer's existing records to inject into
 * the AI prompt. Keeps it small (~30 lines) — enough for the AI to spot conflicts
 * without wasting context on every field.
 *
 * Isolated here (no LLM SDK import) so client components can import it safely.
 */
export function buildExistingProfileContext(existing: FullSeafarerData): string {
  const lines: string[] = ['=== EXISTING RECORDS ON FILE FOR THIS SEAFARER ==='];

  const p = existing.profile;
  if (p?.fullName) lines.push(`Name: ${p.fullName}`);
  if (p?.indosNumber) lines.push(`INDOS: ${p.indosNumber}`);
  if (p?.currentRank) lines.push(`Rank (latest on file): ${p.currentRank}`);

  // Key documents
  const docs = existing.authorisedDocuments ?? [];
  if (docs.length) {
    lines.push('');
    lines.push('DOCUMENTS:');
    for (const d of docs) {
      const expiry = d.validUpto ? ` (expires ${d.validUpto})` : '';
      lines.push(`  - ${d.documentType}: ${d.documentNumber || 'no number'}${expiry}`);
    }
  }

  // CoC
  const cocs = existing.certificatesOfCompetency ?? [];
  if (cocs.length) {
    lines.push('');
    lines.push('CERTIFICATES OF COMPETENCY:');
    for (const c of cocs) {
      const expiry = c.validUpto ? ` (expires ${c.validUpto})` : '';
      lines.push(`  - ${c.grade}: No. ${c.certificateNumber || 'unknown'}${expiry}`);
    }
  }

  // Key STCW courses (last 10)
  const courses = existing.modularCourses ?? [];
  if (courses.length) {
    lines.push('');
    lines.push(`STCW COURSES (${courses.length} on file, showing last 10):`);
    for (const c of courses.slice(-10)) {
      const expiry = c.validUpto ? `, expires ${c.validUpto}` : '';
      lines.push(`  - ${c.courseName}: ${c.certificateNo || 'no cert no'}${expiry}`);
    }
  }

  // Recent sea service
  const svc = existing.seaGoingService ?? [];
  if (svc.length) {
    lines.push('');
    lines.push(`SEA SERVICE (${svc.length} voyages on file, showing last 3):`);
    for (const s of svc.slice(-3)) {
      lines.push(`  - ${s.shipName} (${s.rank}): ${s.serviceFrom}–${s.serviceTo}`);
    }
  }

  lines.push('');
  lines.push('FLAG any record where the PDF shows a DIFFERENT number, date, or grade for the same item. Add to "discrepancies" array.');
  lines.push('=== END EXISTING RECORDS ===');

  return lines.join('\n');
}
